// src/PublicLeaderboard.js
//
// Public, no-auth technician leaderboard at /leaderboard.
//
// Design goals:
//   • Mobile-first — almost always opened on a phone.
//   • Hudson navy header → "Thank you for making our customers smile"
//     line → time-window toggle (All time / This month) → big celebratory
//     podium with $100 / $50 / $25 bonus pills → full standings below
//     → recent shout-outs → minimal footer.
//   • Tie handling: techs with the same count get the same competition
//     rank ("TIED" badge) and share the same bonus tier.
//   • Single control in the header: a refresh button.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaSyncAlt,
  FaCrown,
  FaTrophy,
  FaCommentDots,
  FaChevronDown,
} from 'react-icons/fa';
import API_BASE_URL from './config/api';

const HUDSON_LOGO = '/sidebar.png';
const HUDSON_LOGO_MIN = '/sidebar-min.png';

const BONUS_BY_RANK = {
  1: { amount: '$100', medal: '#d4a017', bg: '#fff7d6', border: '#f3d260', glow: '0 12px 32px rgba(212,160,23,.35)' },
  2: { amount: '$50',  medal: '#8a96a8', bg: '#eef1f6', border: '#cbd5e1', glow: '0 12px 32px rgba(148,163,184,.28)' },
  3: { amount: '$25',  medal: '#b87333', bg: '#fbe9d7', border: '#e3b27e', glow: '0 12px 32px rgba(184,115,51,.28)' },
};

const C = {
  navy: '#1F3A68',
  navyDeep: '#152a4d',
  navySoft: '#2C4E84',
  bgTop: 'linear-gradient(180deg, #1F3A68 0%, #2C4E84 280px, #F2F4F8 280px, #F2F4F8 100%)',
  ink: '#152a4d',
  inkSoft: '#34425E',
  muted: '#5B6A86',
  line: '#E0E5EE',
  card: '#ffffff',
  cardSoft: '#F8FAFD',
  bonusBg: '#dbf4e2',
  bonusBorder: '#9ee0b1',
  bonusInk: '#16774a',
  gold: '#d4a017',
  tieBg: '#FFF4E0',
  tieBorder: '#F0CC73',
  tieInk: '#8B5E08',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 10px 30px rgba(20,30,60,.08)',
  shadowLg: '0 1px 3px rgba(20,30,60,.08), 0 24px 60px rgba(20,30,60,.18)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

const relativeAgo = (input) => {
  if (input == null) return '';
  let secs;
  if (typeof input === 'number') {
    secs = input > 1e12 ? input / 1000 : input;
  } else {
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return '';
    secs = d.getTime() / 1000;
  }
  const diffMin = (Date.now() / 1000 - secs) / 60;
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${Math.round(diffMin)}m ago`;
  if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
  if (diffMin < 60 * 24 * 30) return `${Math.round(diffMin / 60 / 24)}d ago`;
  if (diffMin < 60 * 24 * 365) return `${Math.round(diffMin / 60 / 24 / 30)}mo ago`;
  return `${Math.round(diffMin / 60 / 24 / 365)}y ago`;
};

const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function Stars({ rating = 0, size = 12, color = C.gold }) {
  const out = [];
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  for (let i = 1; i <= 5; i++) {
    if (r >= i) out.push(<FaStar key={i} style={{ color, fontSize: size }} />);
    else if (r >= i - 0.5) out.push(<FaStarHalfAlt key={i} style={{ color, fontSize: size }} />);
    else out.push(<FaRegStar key={i} style={{ color: '#e5e7eb', fontSize: size }} />);
  }
  return <span style={{ display: 'inline-flex', gap: 1.5 }}>{out}</span>;
}

// Assign a competition rank to a sorted-by-count list. Ties share the
// same rank; the next non-tied row jumps past them.
//   counts [5, 5, 3, 2] → ranks [1, 1, 3, 4]
function withCompetitionRanks(list) {
  let lastCount = null;
  let lastRank = 0;
  return list.map((row, idx) => {
    let rank;
    if (row.count === lastCount) {
      rank = lastRank;
    } else {
      rank = idx + 1;
      lastCount = row.count;
      lastRank = rank;
    }
    return { ...row, rank };
  });
}

export default function PublicLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Selected time window: 'all' (all-time) or a month key like '2026-07'.
  // null means "not chosen yet" — we default to the most recent month once
  // the data (and its month list) arrives.
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async (mode = 'load') => {
    // mode: 'load' (initial / browser refresh) | 'manual' (refresh button) | 'poll' (auto 5-min)
    // 'load' and 'manual' send force=1 which tells the backend to kick off
    // a Google sync — but that sync now runs in the BACKGROUND, so the
    // response returns the already-archived data instantly (fast paint).
    // We then schedule one follow-up fetch a few seconds later to surface
    // any brand-new reviews the background sync just pulled.
    if (mode === 'manual') setRefreshing(true);
    else if (mode === 'load') setLoading(true);
    try {
      const force = mode === 'poll' ? '' : '?force=1';
      const r = await fetch(
        `${API_BASE_URL}/api/reviews/internal/leaderboard${force}`,
      );
      const d = await r.json();
      if (!d.ok && !d.leaderboard) {
        throw new Error(d.error || 'Could not load leaderboard');
      }
      setData(d);
      setError(null);
      if (mode !== 'poll') {
        // Give the background sync a moment, then pull the fresh result.
        setTimeout(() => fetchData('poll'), 4000);
      }
    } catch (e) {
      setError(e.message || 'Could not load leaderboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData('load');
    const id = setInterval(() => fetchData('poll'), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Hudson · Top Reviews';
    return () => { document.title = prev; };
  }, []);

  const company = data?.company || {};
  const monthMeta = data?.month || {};
  const highlights = data?.highlights || [];

  const rankedAll = useMemo(
    () => withCompetitionRanks(data?.leaderboard || []),
    [data],
  );

  // Month options for the picker (newest first). Falls back to the legacy
  // single current-month payload if the backend hasn't shipped `months` yet.
  const months = useMemo(() => {
    if (data?.months?.length) return data.months;
    if (data?.monthLeaderboard && data?.month) {
      return [{
        key: 'current',
        label: data.month.label,
        leaderboard: data.monthLeaderboard,
        totalReviews: data.month.totalReviews,
      }];
    }
    return [];
  }, [data]);

  // Default to the most recent month once data loads (unless the user has
  // already picked something).
  useEffect(() => {
    if (selected == null && months.length) setSelected(months[0].key);
  }, [months, selected]);

  const effSelected = selected != null ? selected : (months[0]?.key || 'all');
  const isAll = effSelected === 'all';
  const selectedMonth = isAll ? null : months.find((m) => m.key === effSelected);

  const board = useMemo(() => {
    if (isAll) return rankedAll;
    return withCompetitionRanks(selectedMonth?.leaderboard || []);
  }, [isAll, rankedAll, selectedMonth]);

  const podium = board.filter((r) => r.rank <= 3);
  const rest = board.filter((r) => r.rank > 3);

  if (loading) {
    return (
      <PageShell>
        <div style={{ textAlign: 'center', padding: 64, color: '#fff' }}>
          <FaSyncAlt className="hudson-spin" style={{ fontSize: 26, marginBottom: 10 }} />
          <div>Loading leaderboard…</div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div
          style={{
            background: C.card, borderRadius: 16, padding: 28, textAlign: 'center',
            boxShadow: C.shadow, color: C.ink, maxWidth: 420, margin: '40px auto',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            Couldn't load the leaderboard
          </div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>{error}</div>
          <button type="button" onClick={() => fetchData('load')} style={primaryBtn}>
            Try again
          </button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      onRefresh={() => fetchData('manual')}
      refreshing={refreshing}
      monthLabel={isAll ? 'All time' : (selectedMonth?.label || monthMeta.label)}
      company={company}
      months={months}
      selected={effSelected}
      onSelectChange={setSelected}
    >
      {/* Thank-you headline */}
      <div style={thanksLine}>
        Thank you for making our customers smile.
      </div>

      {/* Podium */}
      {podium.length > 0 ? (
        <Podium podium={podium} />
      ) : (
        <div
          style={{
            background: C.card, borderRadius: 18, padding: '40px 18px',
            textAlign: 'center', boxShadow: C.shadowLg, marginTop: 12,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, marginBottom: 6 }}>
            No technicians on the board {isAll ? 'yet' : `for ${selectedMonth?.label || 'this month'}`}
          </div>
          <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.55 }}>
            As soon as a review names a technician, they'll show up here.
            {!isAll && (rankedAll?.length || 0) > 0 && (
              <>
                {' '}Switch to All time to see the full standings.
              </>
            )}
          </div>
        </div>
      )}

      {/* Full standings */}
      {rest.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <SectionLabel>Also on the board</SectionLabel>
          <div style={{
            background: C.card, borderRadius: 16, boxShadow: C.shadow,
            overflow: 'hidden',
          }}>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {rest.map((row, idx) => (
                <li key={`${row.tech}-${row.rank}`}>
                  <LbRow
                    row={row}
                    last={idx === rest.length - 1}
                    showTied={countTies(board, row.rank) > 1}
                  />
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Recent shout-outs */}
      {highlights.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <SectionLabel icon={<FaCommentDots />}>Recent shout-outs</SectionLabel>
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}>
            {highlights.map((h, i) => <HighlightCard key={i} h={h} />)}
          </div>
        </div>
      )}

      <Footer
        company={company}
        archivedCount={company.archivedCount}
        lastSyncAt={company.lastSyncAt}
      />

      <style>{`
        @keyframes hudsonSpin { to { transform: rotate(360deg); } }
        .hudson-spin { animation: hudsonSpin 1s linear infinite; display: inline-block; }
        @keyframes hudsonPulse {
          0%, 100% { transform: translateX(-50%) scale(1); }
          50% { transform: translateX(-50%) scale(1.06); }
        }
        .pulse-crown { animation: hudsonPulse 2.4s ease-in-out infinite; transform-origin: center; }
        @media (max-width: 720px) {
          .header-title { font-size: 22px !important; }
          .header-month { font-size: 11px !important; }
        }
      `}</style>
    </PageShell>
  );
}

function countTies(board, rank) {
  return board.filter((r) => r.rank === rank).length;
}

// ─── Page shell ────────────────────────────────────────────────────────────

function PageShell({
  children, onRefresh, refreshing, monthLabel, company,
  months, selected, onSelectChange,
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.bgTop,
        fontFamily: sharedFont,
        color: C.ink,
        padding: '20px 14px 40px',
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, background: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,.18)', flexShrink: 0, overflow: 'hidden',
            }}>
              <img
                src={HUDSON_LOGO_MIN}
                alt="Hudson"
                style={{ width: 34, height: 34, objectFit: 'contain' }}
                onError={(e) => { e.currentTarget.src = HUDSON_LOGO; }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="header-title" style={{
                color: '#fff', fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em',
                lineHeight: 1.1, whiteSpace: 'nowrap',
              }}>
                Top Reviews
              </div>
              <div className="header-month" style={{
                color: 'rgba(255,255,255,.78)', fontSize: 12, fontWeight: 700,
                letterSpacing: '.08em', textTransform: 'uppercase', marginTop: 3,
              }}>
                {monthLabel || 'Live leaderboard'}
              </div>
              {company && (typeof company.totalGoogleReviews === 'number' || typeof company.averageRating === 'number') && (
                <div style={{ marginTop: 5 }}>
                  {typeof company.totalGoogleReviews === 'number' && (
                    <div style={{
                      color: 'rgba(255,255,255,.92)', fontSize: 12, fontWeight: 700,
                      lineHeight: 1.2, whiteSpace: 'nowrap',
                    }}>
                      {(company.totalGoogleReviews || 0).toLocaleString()} Google reviews
                    </div>
                  )}
                  {typeof company.averageRating === 'number' && (
                    <div style={{
                      color: 'rgba(255,255,255,.92)', fontSize: 12, fontWeight: 700,
                      marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 6,
                      whiteSpace: 'nowrap',
                    }}>
                      <Stars rating={company.averageRating} size={11} color="#FFD262" />
                      <span>{company.averageRating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
          }}>
            {onSelectChange && (
              <MonthSelect
                value={selected}
                onChange={onSelectChange}
                months={months || []}
              />
            )}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="Refresh"
                title="Refresh"
                style={{
                  background: 'rgba(255,255,255,.18)',
                  border: '1px solid rgba(255,255,255,.3)',
                  color: '#fff',
                  width: 36, height: 36, borderRadius: 10,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: 14, flexShrink: 0,
                  backdropFilter: 'blur(6px)',
                }}
              >
                <FaSyncAlt className={refreshing ? 'hudson-spin' : ''} />
              </button>
            )}
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}

// ─── Month picker (header) ─────────────────────────────────────────────────
//
// Pill-shaped native <select> listing every month that has reviews (newest
// first) plus an "All time" option, replacing the old month/All toggle so the
// board can be scoped to any past month.

function MonthSelect({ value, onChange, months }) {
  return (
    <div style={{
      position: 'relative',
      background: 'rgba(255,255,255,.18)',
      border: '1px solid rgba(255,255,255,.28)',
      borderRadius: 999,
      display: 'inline-flex', alignItems: 'center',
      backdropFilter: 'blur(6px)',
    }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter by month"
        style={{
          background: 'transparent',
          color: '#fff',
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          border: 'none',
          outline: 'none',
          cursor: 'pointer',
          padding: '7px 26px 7px 12px',
          borderRadius: 999,
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
      >
        <option value="all" style={{ color: '#111' }}>All time</option>
        {months.map((m) => (
          <option key={m.key} value={m.key} style={{ color: '#111' }}>
            {m.label}
          </option>
        ))}
      </select>
      <FaChevronDown
        style={{
          position: 'absolute', right: 10, fontSize: 9,
          color: '#fff', pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// ─── Olympic-style Podium ────────────────────────────────────────────────
//
// Classic three-pillar podium. Pillars laid out [#2, #1, #3] left-to-right
// so the tallest gold pillar sits in the middle. Each pillar's tech card
// (avatar + name + count) hovers above the pillar; the pillar itself
// shows the rank digit and bonus.
//
// Ties on a single rank stack vertically inside that pillar's column.
// Empty pillars (no tech for that rank yet) render as a soft "—"
// placeholder so the podium silhouette stays intact.

const PODIUM_HEIGHTS = { 1: 170, 2: 120, 3: 88 };
const PODIUM_DIGITS  = { 1: 88,  2: 64,  3: 52 };

function Podium({ podium }) {
  const byRank = { 1: [], 2: [], 3: [] };
  for (const p of podium) {
    if (byRank[p.rank]) byRank[p.rank].push(p);
  }
  // Render pillars in Olympic order: 2, 1, 3 (silver, gold, bronze).
  const slots = [2, 1, 3];

  return (
    <div
      style={{
        background: C.card, borderRadius: 18, padding: '32px 14px 0',
        boxShadow: C.shadowLg, marginTop: 12, overflow: 'hidden',
      }}
    >
      <div
        className="podium-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.2fr 1fr',
          gap: 6,
          alignItems: 'end',
        }}
      >
        {slots.map((rank) => (
          <PodiumPillar
            key={rank}
            rank={rank}
            entries={byRank[rank]}
          />
        ))}
      </div>
    </div>
  );
}

function PodiumPillar({ rank, entries }) {
  const cfg = BONUS_BY_RANK[rank];
  const isFirst = rank === 1;
  const hasEntries = entries.length > 0;
  const tied = entries.length > 1;
  const height = PODIUM_HEIGHTS[rank];
  const digit = PODIUM_DIGITS[rank];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch',
      minWidth: 0,
    }}>
      {/* Stack of tech cards sitting above the pillar */}
      <div style={{
        textAlign: 'center', padding: '0 2px 12px',
        display: 'flex', flexDirection: 'column', gap: 14,
        alignItems: 'center', minHeight: 96,
      }}>
        {hasEntries ? (
          <>
            {tied && (
              <span style={{
                background: C.tieBg, border: `1px solid ${C.tieBorder}`,
                color: C.tieInk, fontWeight: 800, fontSize: 9,
                letterSpacing: '.1em', padding: '2px 8px', borderRadius: 999,
                textTransform: 'uppercase', whiteSpace: 'nowrap',
              }}>
                Tied · {entries.length}-way
              </span>
            )}
            {entries.map((e) => (
              <PodiumTech key={e.tech} entry={e} cfg={cfg} isFirst={isFirst} />
            ))}
          </>
        ) : (
          <div style={{
            color: C.muted, fontSize: 12, fontWeight: 700, opacity: .6,
          }}>—</div>
        )}
      </div>

      {/* The pillar itself */}
      <div style={{
        height,
        background: `linear-gradient(180deg, ${cfg.bg} 0%, ${cfg.border} 100%)`,
        borderTopLeftRadius: 14, borderTopRightRadius: 14,
        borderTop: `3px solid ${cfg.medal}`,
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        paddingTop: 6,
      }}>
        <div style={{
          fontSize: digit, fontWeight: 900, color: cfg.medal,
          letterSpacing: '-0.04em', lineHeight: 1,
          textShadow: `0 2px 0 rgba(255,255,255,.6)`,
        }}>
          {rank}
        </div>
        <div style={{
          position: 'absolute', bottom: 8, left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'rgba(255,255,255,.85)', color: cfg.medal,
            border: `1px solid ${cfg.medal}`, fontWeight: 800,
            fontSize: isFirst ? 12 : 10, padding: '3px 9px', borderRadius: 999,
            whiteSpace: 'nowrap',
          }}>
            <FaTrophy style={{ fontSize: isFirst ? 10 : 9 }} /> {cfg.amount}
          </span>
        </div>
      </div>
    </div>
  );
}

function PodiumTech({ entry, cfg, isFirst }) {
  return (
    <div style={{
      textAlign: 'center', position: 'relative',
      minWidth: 0, maxWidth: '100%',
    }}>
      {isFirst && (
        <FaCrown
          className="pulse-crown"
          style={{
            position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)',
            color: cfg.medal, fontSize: 20,
          }}
        />
      )}
      <div
        style={{
          width: isFirst ? 72 : 56, height: isFirst ? 72 : 56, borderRadius: '50%',
          background: '#fff', color: cfg.medal, margin: '0 auto 8px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: `3px solid ${cfg.border}`, boxShadow: cfg.glow,
          fontWeight: 800, fontSize: isFirst ? 22 : 18, letterSpacing: '-0.02em',
        }}
      >
        {initials(entry.tech)}
      </div>
      <div style={{
        fontWeight: 800, color: C.ink, fontSize: isFirst ? 15 : 13,
        marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', padding: '0 2px',
      }} title={entry.tech}>
        {entry.tech}
      </div>
      <div style={{
        fontSize: 11, color: C.muted, fontWeight: 700,
        lineHeight: 1.25,
      }}>
        {entry.count} review{entry.count === 1 ? '' : 's'}
        {typeof entry.avgRating === 'number' ? ` · ${entry.avgRating.toFixed(1)}★` : ''}
      </div>
    </div>
  );
}

// ─── Standings row (rank 4+) ──────────────────────────────────────────────

function LbRow({ row, last, showTied }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 14px',
      borderBottom: last ? 'none' : `1px solid ${C.line}`,
    }}>
      <div style={{
        width: 32, color: C.muted, fontWeight: 700, fontSize: 13, textAlign: 'center',
        flexShrink: 0,
      }}>
        {row.rank}
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', background: C.cardSoft,
        color: C.navy, fontWeight: 800, fontSize: 13,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${C.line}`, flexShrink: 0,
      }}>
        {initials(row.tech)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          overflow: 'hidden',
        }}>
          <span style={{
            fontSize: 15, fontWeight: 700, color: C.ink,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row.tech}
          </span>
          {showTied && (
            <span style={{
              background: C.tieBg, border: `1px solid ${C.tieBorder}`,
              color: C.tieInk, fontWeight: 800, fontSize: 9,
              letterSpacing: '.1em', padding: '2px 6px', borderRadius: 999,
              textTransform: 'uppercase', flexShrink: 0,
            }}>
              Tied
            </span>
          )}
        </div>
        <div style={{
          fontSize: 11, color: C.muted, marginTop: 2,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          {typeof row.avgRating === 'number' && (
            <>
              <Stars rating={row.avgRating} size={10} />
              {row.avgRating.toFixed(1)}
            </>
          )}
          {row.lastMentionedEpoch && (
            <>
              <span>·</span>
              <span>last {relativeAgo(row.lastMentionedEpoch)}</span>
            </>
          )}
        </div>
      </div>
      <div style={{
        textAlign: 'right', flexShrink: 0, fontWeight: 800, color: C.ink, fontSize: 18,
        lineHeight: 1.05,
      }}>
        {row.count}
        <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase' }}>
          {row.count === 1 ? 'review' : 'reviews'}
        </div>
      </div>
    </div>
  );
}

// ─── Highlight card ───────────────────────────────────────────────────────

function HighlightCard({ h }) {
  const author = h.authorName || 'Anonymous';
  return (
    <div style={{
      background: C.card, borderRadius: 14, padding: '14px 16px',
      boxShadow: C.shadow,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <div style={{ fontWeight: 700, color: C.ink, fontSize: 14 }}>{author}</div>
        <Stars rating={h.rating || 0} size={12} />
      </div>
      <div style={{
        fontSize: 13, color: C.inkSoft, lineHeight: 1.55,
        display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {h.text}
      </div>
      {h.mentionedTechs && h.mentionedTechs.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {h.mentionedTechs.map((t) => (
            <span key={t} style={{
              background: '#fdf3df', color: '#92400e', fontSize: 11,
              fontWeight: 700, padding: '3px 9px', borderRadius: 999,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <FaTrophy style={{ fontSize: 9 }} /> {t}
            </span>
          ))}
        </div>
      )}
      <div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>
        {relativeAgo(h.publishTime)}
      </div>
    </div>
  );
}

// ─── Section label ───────────────────────────────────────────────────────

function SectionLabel({ children, icon }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontWeight: 700, fontSize: 12, color: C.muted,
      textTransform: 'uppercase', letterSpacing: '.1em',
      paddingLeft: 4, marginBottom: 10,
    }}>
      {icon}{children}
    </div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────

function Footer({ archivedCount, lastSyncAt }) {
  return (
    <div
      style={{
        marginTop: 32,
        textAlign: 'center',
        padding: '0 8px',
      }}
    >
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, marginBottom: 8,
      }}>
        <img
          src={HUDSON_LOGO}
          alt="Hudson Condominium Solutions Inc"
          style={{ height: 28, objectFit: 'contain' }}
          onError={(e) => { e.currentTarget.src = HUDSON_LOGO_MIN; }}
        />
      </div>
      <div style={{
        color: C.muted, fontSize: 11, lineHeight: 1.55,
      }}>
        Updates automatically every 5 minutes
        {lastSyncAt ? ` · Last poll ${relativeAgo(lastSyncAt)}` : ''}
        {typeof archivedCount === 'number' ? ` · ${archivedCount} archived` : ''}
      </div>
    </div>
  );
}

const primaryBtn = {
  background: C.navy, color: '#fff', border: 'none', borderRadius: 10,
  padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 8,
};

const thanksLine = {
  textAlign: 'center',
  color: '#fff',
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: '-0.01em',
  margin: '20px auto 28px',
  padding: '6px 8px',
  maxWidth: 560,
  lineHeight: 1.4,
};
